import { ArrowLeft } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useSmartBack } from '@/hooks/useSmartBack';

interface SoftBackButtonProps {
  /** Explicit label override, e.g. "Back to Inventory". When omitted, derived from referrer/state. */
  label?: string;
  /** Label to use when no contextual label can be derived. Defaults to "Back". */
  fallbackLabel?: string;
  /** Where to navigate if there's no usable history. */
  fallbackPath?: string;
  /** Force-navigate to this path instead of using history. */
  to?: string;
  /** Custom click handler. Takes precedence over `to` and history navigation. */
  onClick?: () => void;
  className?: string;
}

const ROUTE_LABELS: Array<{ test: (p: string) => boolean; label: (p: string) => string }> = [
  { test: (p) => p === '/inventory' || p.startsWith('/inventory'), label: () => 'Back to Inventory' },
  { test: (p) => p === '/market' || p.startsWith('/market'), label: () => 'Back to Market' },
  { test: (p) => p === '/activity' || p.startsWith('/activity'), label: () => 'Back to Activity' },
  { test: (p) => p === '/events' || p.startsWith('/events'), label: () => 'Back to Events' },
  { test: (p) => p === '/sets' || p.startsWith('/sets'), label: () => 'Back to Sets' },
  { test: (p) => p === '/decks' || p.startsWith('/decks'), label: () => 'Back to Decks' },
  { test: (p) => p === '/shows' || p.startsWith('/shows') || p.startsWith('/show/'), label: () => 'Back to Shows' },
  { test: (p) => p === '/menus' || p.startsWith('/menus'), label: () => 'Back to Menus' },
  { test: (p) => p.startsWith('/ebay/'), label: () => 'Back to eBay' },
  { test: (p) => p === '/transactions', label: () => 'Back to Transactions' },
  { test: (p) => p === '/settings', label: () => 'Back to Settings' },
  { test: (p) => p === '/profile' || p.startsWith('/profile'), label: () => 'Back to Profile' },
  { test: (p) => /^\/item\//.test(p), label: () => 'Back to Item' },
  {
    test: (p) => /^\/u\/[^/]+/.test(p),
    label: (p) => `Back to @${p.match(/^\/u\/([^/?#]+)/)?.[1] ?? ''}`,
  },
];

function matchLabel(path: string | null | undefined): string | null {
  if (!path) return null;
  for (const entry of ROUTE_LABELS) {
    if (entry.test(path)) return entry.label(path);
  }
  return null;
}

function deriveFromReferrer(): string | null {
  try {
    const ref = typeof document !== 'undefined' ? document.referrer : '';
    if (!ref) return null;
    const url = new URL(ref);
    if (typeof window === 'undefined' || url.origin !== window.location.origin) return null;
    return matchLabel(url.pathname);
  } catch {
    return null;
  }
}

export function SoftBackButton({
  label,
  fallbackLabel = 'Back',
  fallbackPath = '/',
  to,
  onClick,
  className,
}: SoftBackButtonProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { goBack, previousPath } = useSmartBack(fallbackPath);

  // Priority: explicit label → tracked referrer → document.referrer → fallback
  const resolvedLabel =
    label ?? matchLabel(previousPath) ?? deriveFromReferrer() ?? fallbackLabel;

  const handleClick = () => {
    if (onClick) {
      onClick();
      return;
    }
    if (to) {
      navigate(to);
      return;
    }
    goBack();
  };

  // location is referenced so React re-evaluates label on route change.
  void location.pathname;

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        'inline-flex items-center gap-1.5 h-8 px-2.5 -ml-1 rounded-full',
        'text-xs text-muted-foreground hover:text-foreground hover:bg-white/[0.06]',
        'transition-colors',
        className,
      )}
      aria-label={resolvedLabel}
    >
      <ArrowLeft className="h-3.5 w-3.5" />
      {resolvedLabel}
    </button>
  );
}
